import { useState, useEffect, useCallback } from 'react';

const slides = [
  {
    id: 1,
    text: 'Sofreu acidente',
    sub: 'Acidente de trabalho ou trânsito pode garantir benefício do INSS',
    image: 'https://readdy.ai/api/search-image?query=dramatic%20close%20up%20of%20bandaged%20hand%20resting%20on%20table%2C%20soft%20warm%20window%20light%2C%20blurred%20hospital%20or%20home%20background%2C%20calm%20and%20hopeful%20atmosphere%2C%20documentary%20style%20photography%2C%20muted%20warm%20tones%2C%20no%20graphic%20content%2C%20subtle%20and%20dignified%20image&width=520&height=620&seq=carousel-slide-1-accident&orientation=portrait',
    icon: 'ri-health-book-line',
  },
  {
    id: 2,
    text: 'Trabalhou exposto a agentes nocivos ou perigosos',
    sub: 'Aposentadoria especial para quem trabalhou em condições insalubres',
    image: 'https://readdy.ai/api/search-image?query=industrial%20worker%20in%20safety%20helmet%20and%20protective%20gear%20working%20in%20factory%20environment%2C%20dramatic%20industrial%20lighting%2C%20steel%20and%20metal%20machinery%20background%2C%20serious%20atmosphere%2C%20documentary%20style%2C%20warm%20tones%20with%20industrial%20haze%2C%20dignified%20labor%20photography&width=520&height=620&seq=carousel-slide-2-hazard&orientation=portrait',
    icon: 'ri-shield-flash-line',
  },
  {
    id: 3,
    text: 'Trabalha no meio rural',
    sub: 'Aposentadoria rural com regras diferenciadas para trabalhadores do campo',
    image: 'https://readdy.ai/api/search-image?query=rural%20farm%20worker%20in%20Brazilian%20countryside%2C%20golden%20hour%20sunlight%20over%20vast%20agricultural%20field%2C%20weathered%20hands%20holding%20soil%2C%20warm%20earthy%20tones%2C%20dignified%20and%20peaceful%20rural%20atmosphere%2C%20cinematic%20documentary%20photography%2C%20green%20and%20amber%20color%20palette&width=520&height=620&seq=carousel-slide-3-rural&orientation=portrait',
    icon: 'ri-plant-line',
  },
  {
    id: 4,
    text: 'Possui algum tipo de deficiência',
    sub: 'BPC/LOAS e aposentadoria para pessoas com deficiência',
    image: 'https://readdy.ai/api/search-image?query=person%20in%20wheelchair%20near%20large%20window%20with%20soft%20natural%20light%2C%20calm%20and%20empowering%20atmosphere%2C%20modern%20interior%20background%2C%20hopeful%20expression%2C%20warm%20neutral%20tones%2C%20dignified%20lifestyle%20photography%2C%20inclusive%20and%20positive%20representation&width=520&height=620&seq=carousel-slide-4-disability&orientation=portrait',
    icon: 'ri-heart-pulse-line',
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  const advance = useCallback((next: number | ((prev: number) => number)) => {
    setFading(true);
    setTimeout(() => {
      setCurrent(next);
      setFading(false);
    }, 350);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setFading(false);
      }, 350);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const handleDot = (idx: number) => {
    if (idx === current) return;
    advance(idx);
  };

  return (
    <section className="min-h-screen bg-brand-warm flex flex-col" style={{ paddingTop: '72px' }}>
      {/* Main content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-8 flex flex-col lg:flex-row items-stretch gap-0">

        {/* Left: Text column */}
        <div className="flex-1 flex flex-col justify-center py-20 pr-0 lg:pr-20">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-px bg-brand-gold" />
            <span className="font-inter text-xs font-medium text-brand-gold uppercase tracking-[0.2em]">
              Direito Previdenciário
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-cormorant text-5xl lg:text-6xl xl:text-7xl font-semibold text-brand-dark leading-[1.08] mb-8">
            Defendemos seu
            <br />
            direito a <span className="italic text-brand-gold">benefícios</span>
            <br />
            previdenciários
          </h1>

          <p className="font-inter text-base text-brand-muted leading-relaxed max-w-md mb-10">
            Atendimento especializado em Direito Previdenciário para garantir o que é seu por direito — com transparência, experiência e dedicação real ao seu caso.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-16">
            <a
              href="https://api.whatsapp.com/send?phone=5551996057679&text=Ol%C3%A1,%20vim%20do%20seu%20site%20e%20gostaria%20de%20falar%20com%20um%20advogado."
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-white font-inter font-medium text-sm px-7 py-3.5 rounded-md transition-all duration-200"
            >
              <i className="ri-whatsapp-line text-base" />
              Falar com especialista
            </a>
            <button
              onClick={() => {
                const el = document.querySelector('#servicos');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 text-brand-dark border border-brand-border hover:border-brand-gold hover:text-brand-gold font-inter font-medium text-sm px-7 py-3.5 rounded-md transition-all duration-200"
            >
              Ver serviços
            </button>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-10 pt-8 border-t border-brand-border">
            {[
              { value: '15+', label: 'Anos de experiência' },
              { value: '98%', label: 'Casos resolvidos' },
              { value: 'Todo Brasil', label: 'Atendimento remoto' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-cormorant text-3xl font-semibold text-brand-dark leading-none mb-1">
                  {stat.value}
                </p>
                <p className="font-inter text-xs text-brand-muted">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Carousel column */}
        <div className="hidden lg:flex flex-shrink-0 w-[460px] xl:w-[520px] items-end pt-10">
          <div className="relative w-full h-full" style={{ minHeight: '500px', maxHeight: '620px' }}>
            {/* Gold frame decoration */}
            <div className="absolute top-8 right-0 left-8 bottom-0 border border-brand-gold/20 rounded-xl z-0" />

            {/* Carousel */}
            <div className="relative z-10 w-full overflow-hidden rounded-xl" style={{ height: '580px' }}>
              {/* Slide image */}
              <div
                className="w-full h-full transition-opacity duration-350"
                style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.35s ease' }}
              >
                <img
                  src={slides[current].image}
                  alt={slides[current].text}
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* Dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

              {/* Text overlay */}
              <div
                className="absolute inset-x-0 bottom-0 px-8 pb-10"
                style={{
                  opacity: fading ? 0 : 1,
                  transform: fading ? 'translateY(8px)' : 'translateY(0)',
                  transition: 'opacity 0.35s ease, transform 0.35s ease',
                }}
              >
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-gold/20 border border-brand-gold/40 mb-4">
                  <i className={`${slides[current].icon} text-brand-gold text-lg`} />
                </div>

                {/* Main text */}
                <p className="font-cormorant text-3xl font-semibold text-white leading-tight mb-2">
                  {slides[current].text}
                </p>

                {/* Sub text */}
                <p className="font-inter text-sm text-white/70 leading-relaxed">
                  {slides[current].sub}
                </p>

                {/* Dots */}
                <div className="flex items-center gap-2 mt-6">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleDot(idx)}
                      className={`cursor-pointer rounded-full transition-all duration-300 ${idx === current
                          ? 'w-6 h-2 bg-brand-gold'
                          : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                        }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom trust strip */}
      <div className="border-t border-brand-border bg-white">
        <div className="max-w-7xl mx-auto px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <p className="font-inter text-xs text-brand-muted">Especialista em benefícios negados pelo INSS</p>
          <div className="flex items-center gap-8">
            {['BPC/LOAS', 'Aposentadoria', 'Auxílio-Doença', 'Aposentadoria Rural'].map((s) => (
              <span key={s} className="font-inter text-xs text-brand-charcoal/50 whitespace-nowrap">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
